import * as React from 'react';

import * as Yup from 'yup';
import moment, { Moment } from 'moment';
import Tag from 'antd/es/tag';
import Tooltip from 'antd/es/tooltip';

import { phoneFormat } from '@common/react/components/Forms/FormikPhoneControl/FormikPhoneControl';
import { phoneRegexp } from '@common/react/utils/validationHelpers';
import dummyAvatar from '@common/react/images/avatar.jpg';
import { replaceSearchUrl } from '@common/react/components/Utils/Utils';
import { getUserNameOrUnnamedWithEmail, Named } from '@common/react/utils/utils';

import { Gender } from '@commonTuna/react/objects/Enums';

import { Patient } from '@app/objects/Patient';
import { DayOffStatus } from '@app/objects/Appointment';
import { Doctor } from '@app/objects/Doctor';
import { extensions } from '@app/components/UI/SendEmailModal/SendEmailReadonly';

interface WithAvatar {
	avatar: string;
}

export const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'heic', 'heif', 'bmp'];

export const acceptImages = imageExtensions.filter((ext) => ext !== 'gif').map((extensions) => `.${extensions}`).join(', ');

export const capitalizeFirstLetter = (string: string) => {
	return string[0].toUpperCase() + string.slice(1);
};

export const dateToUtc = (date: number | null): Moment => {
	const isGlobalDst = moment(new Date()).isDST();
	const isCurrentDateDst = moment(date).isDST();
	const offset = moment().utcOffset();

	const correctlyOffset = isGlobalDst
		? isCurrentDateDst ? offset : offset - 60
		: isCurrentDateDst ? offset + 60 : offset;
	const momentDate = moment(date);

	// isDST = false - date < 14.03.21, isDST = true - date >= 14.03.21
	return momentDate.subtract(correctlyOffset, 'minutes');
};

export const dateToUtcString = (date: number | null, format: string): string => {
	const utc = dateToUtc(date);
	return moment(utc).format(format);
};

export const getDateFormatTime = (date: number | null, format: string): string => {
	return moment(date).format(format);
};

export const getDashboardContainer = (): HTMLElement => document.getElementById('dashboardContainer') || document.body;

export const helperContainer = (): HTMLElement => document.querySelector('.dashboard-container') || document.body;

export const getCheckoutContainer = (): HTMLElement => document.getElementById('checkoutContainer') || document.body;

export const getPopupContainer = (node: HTMLElement): HTMLElement => node.closest('.ant-popover') || document.body;

export const getEditorContainer = (node: HTMLElement): HTMLElement => node.closest('.editor-page') || document.body;

export const dragContainerProps = {
	helperContainer,
};

export const dateTimeFormatString = 'MM/DD/YYYY h:mm A';
export const dateFormatString = 'MM/DD/YYYY';

export const getFullName = (item?: Named) => {
	if (item) {
		return (!item.lastName && !item.firstName) ? 'Unnamed' : `${item.lastName || ''} ${item.firstName || ''}`;
	}
	return '';
};

export const getFullPatientName = (item?: Named & { middleName?: string, nickName?: string }, showNickName?: boolean) => {
	if (item) {
		return (!item.lastName && !item.firstName) ? 'Unnamed' : `${item.firstName || ''} ${
			item.middleName ? `${item.middleName} ` : ''}${item.lastName || ''}${
			item.nickName && showNickName ? ` (${item.nickName})` : ''}`;
	}
	return '';
};

export const getFullPatientInfo = (item?: Patient) => {
	if (item) {
		let result = getUserNameOrUnnamedWithEmail(item);
		result += item.cellPhone ? `, ${phoneFormat(item.cellPhone)}` : '';
		result += item.birthDate ? `, ${moment(item.birthDate).utc().format(dateFormatString)}` : '';

		return result;
	}
	return '';
};

export const getFullPatientInfoWithParent = (item?: Patient) => {
	const parentInfo = item?.parent ? [
		item.parent.email ? `(${item.parent.email})` : '',
		item.parent.cellPhone ? `${phoneFormat(item.parent.cellPhone)}` : '',
		item.parent.birthDate ? `${moment(item.parent.birthDate).utc().format(dateFormatString)}` : '',
	].filter((item) => item).join(', ') : '';
	return <>
		{getFullPatientInfo(item)}
		{item?.parent ? <>
			{' '}
			<Tag>
				{getFullName(item?.parent)}
				<i
					className={`ml10 fa fa-${item.parent.gender === Gender.Female ? 'female' : 'male'}`}
					style={{ transform: 'scale(1.2)' }}
					aria-hidden="true"
				/>
				{parentInfo && <Tooltip title={parentInfo}>
					<i className="fa fa-info-circle ml10" />
				</Tooltip>}
			</Tag>
		</> : null}
	</>;
};

export const timeSpanToMinutes = (timeSpan: string): number | null => {
	const timeArr = timeSpan.split(':');
	if (timeArr.length === 3) {
		const hours = +timeArr[0];
		const minutes = +timeArr[1];
		return hours ? (hours * 60) + minutes : minutes;
	}
	return null;
};

export const getParentWidth = (parentSelector: string): number | undefined => {
	if (typeof document !== 'undefined' && typeof window !== 'undefined') {
		const parentEl = document.querySelector(parentSelector);
		if (!parentEl) {
			return;
		}

		const parentStyle = window.getComputedStyle(parentEl, null);
		if (!parentStyle.width) {
			return;
		}

		return parseInt(parentStyle.width, 10)
			- parseInt(parentStyle.paddingLeft || '', 10) - parseInt(parentStyle.paddingRight || '', 10);
	}
};

export const dataURLtoFile = (dataurl: string, filename: string) => {
	const arr = dataurl.split(',');
	const bstr = atob(arr[1]);
	const match = arr[0].match(/:(.*?);/);
	const mime = match ? match[1] : '';

	let n = bstr.length;

	const u8arr = new Uint8Array(n);

	while (n--) {
		u8arr[n] = bstr.charCodeAt(n);
	}

	return new File([u8arr], filename, { type: mime });
};

export const transformToUtc = (date: number): number => {
	const d = new Date(date);
	return +new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes(), 0, 0));
};

export const phoneOptionalValidator = Yup.string().test('is-valid', 'Invalid phone number', (value) =>
	!value || phoneRegexp.test(value)).nullable().notRequired();

export const groupBy = <T, >(arr: Array<T>, prop: string): Map<string, Array<T>> => {
	const map = new Map<string, Array<T>>();

	arr.forEach((curr) => {
		const value = map.get(curr[prop]) || [];
		value.push(curr);
		map.set(curr[prop], value);
	});

	return map;
};

export const getAvatar: (user: WithAvatar | null, dummyImage?: string, portal?: boolean) => string = (user, dummyImage, portal) => {
	return `${(user && user.avatar)
		? portal ? `/remote/${user.avatar}` : user.avatar
		: dummyImage || dummyAvatar}`;
};

export const numberArrayEquals = (a: Array<number> | null | undefined, b: Array<number> | null | undefined): boolean => {
	if (a === b) return true;
	if (a == null || b == null) return false;
	if (!Array.isArray(a) || !Array.isArray(b)) return false;
	if (a.length !== b.length) return false;

	a.sort();
	b.sort();

	return a.every((val, index) => val === b[index]);
};

export const getFormattedNumber = (num: number | undefined) => {
	return `${num?.toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')}`;
};

export const daysGroup = [
	{ caption: '0 days', value: 0 },
	{ caption: '1 day', value: 1 },
	{ caption: '3 days', value: 3 },
	{ caption: '1 week', value: 7 },
	{ caption: '2 week', value: 14 },
	{ caption: '1 month', value: 30 },
	{ caption: '3 months', value: 90 },
	{ caption: '1 year', value: 365 },
	{ caption: '3 years', value: 1095 },
];

export const wizardLockIntervalDaysGroup = [
	{ caption: 'Never', value: null },
	{ caption: '1 day', value: 1 },
	{ caption: '2 days', value: 2 },
	{ caption: '3 days', value: 3 },
	{ caption: '1 week', value: 7 },
	{ caption: '2 week', value: 14 },
];

export const hoursGroup = [
	{ caption: '0 hours', value: 0 },
	{ caption: '1 hour', value: 1 },
	{ caption: '3 hours', value: 3 },
	{ caption: '5 hours', value: 5 },
	{ caption: '1 day', value: 24 },
	{ caption: '2 days', value: 48 },
	{ caption: '3 days', value: 72 },
	{ caption: '1 week', value: 168 },
	{ caption: '3 weeks', value: 504 },
];

export const statusIntervalHoursGroup = [
	{ caption: '6 hours', value: 6 },
	{ caption: '12 hours', value: 12 },
	{ caption: '1 day', value: 24 },
	{ caption: '2 days', value: 48 },
	{ caption: '3 days', value: 72 },
	{ caption: '1 week', value: 168 },
];

export const frequencyOfReviewRequestDaysGroup = [
	{ caption: '1 month', value: 30 },
	{ caption: '3 months', value: 90 },
	{ caption: '6 months', value: 180 },
	{ caption: '9 months', value: 270 },
	{ caption: '1 year', value: 365 },
];

export const portalLinkExpirationTime = [
	{ caption: '1 day', value: 1 },
	{ caption: '2 days', value: 2 },
	{ caption: '3 days', value: 3 },
	{ caption: '1 week', value: 7 },
	{ caption: '2 weeks', value: 14 },
	{ caption: '3 weeks', value: 21 },
	{ caption: '1 month', value: 30 },
];

export const statusIntervalMinutesGroup = [
	{ caption: '30 minutes', value: 30 },
	{ caption: '1 hour', value: 60 },
	{ caption: '2 hours', value: 120 },
	{ caption: '3 hours', value: 180 },
	{ caption: '4 hours', value: 240 },
	{ caption: '5 hours', value: 300 },
];

export const CommentTypeColor = {
	simple: '#f0f0f0',
	warning: '#fbd4d4',
};

export const sortOptionsByName = (a, b) => {
	if (a.value < 0 || b.value < 0) {
		return a.value - b.value;
	}
	return typeof a?.children === 'string' && b?.children === 'string'
		? a?.children.toLowerCase() < b?.children.toLowerCase() ? -1 : 1
		: 0;
};

export const isTextInclude = (text: string, name: string) => (
	name
		.toLocaleLowerCase()
		.replace(/[-.]/g, '')
		.indexOf(text.toLocaleLowerCase().replace(/[-.]/g, '')) > -1
);

export const getDoctorName = (item?: Doctor) => {
	if (item) {
		const arr = item.nameEn.split(' ');
		const lastName = arr.slice(1).join(' ');
		const firstName = arr[0];

		return {
			lastName,
			firstName,
		};
	}
	return {};
};

export const detectMob = () => {
	const toMatch = [
		/Android/i,
		/webOS/i,
		/iPhone/i,
		/iPad/i,
		/iPod/i,
		/BlackBerry/i,
		/Windows Phone/i,
	];

	return toMatch.some((toMatchItem) => {
		return navigator.userAgent.match(toMatchItem);
	});
};

export const openInNewTab = (link: string) => {
	setTimeout(() => {
		const a = document.createElement('a');
		a.setAttribute('href', link);
		a.setAttribute('target', '_blank');
		a.click();
	}, 0);
};

export const getRandomColor = () => {
	const o = Math.round; const r = Math.random; const
		s = 255;
	const red = o(r() * s);
	const green = o(r() * s);
	const blue = o(r() * s);
	return `#${red < 16 ? '0' : ''}${
		red.toString(16)}${green < 16 ? '0' : ''}${
		green.toString(16)}${blue < 16 ? '0' : ''}${
		blue.toString(16)}`;
};

export const toLoverCamelCase = (str) => {
	return str.replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, (match, index) => {
		if (+match === 0) return ''; // or if (/\s+/.test(match)) for white spaces
		return index === 0 ? match.toLowerCase() : match.toUpperCase();
	});
};

export const redirectToRightTabs = (e, navigate, itemId: number, path: string, location) => {
	if (e.target?.tagName === 'BUTTON' || e.target?.closest('button')) {
		return;
	}
	const search = replaceSearchUrl(location.search, 'right-tabs', path);

	navigate({
		...location,
		pathname: location.pathname,
		search: replaceSearchUrl(search, 'right-tabs-id', `${itemId}`),
	}, { replace: true });
};
